import { lazy, Suspense } from 'react';
import { BrowserRouter, StaticRouter, Routes, Route } from 'react-router-dom';

// ============================================================================
// COMPONENTS
// ============================================================================
import ScrollToTop from './components/ScrollToTop';
import ExternalRedirect from './components/ExternalRedirect';

// Short, brandable press links used on marketing material (video, print, decks)
const FORBES_ARTICLE =
      'https://www.forbes.com/sites/kolawolesamueladebayo/2025/06/27/how-ai-could-reshape-global-education---and-what-comes-after/';

// ============================================================================
// PAGE IMPORTS
// ============================================================================

// Homepage
import Home from './pages/homepage';

// Product Pages
import { landingPages } from './content/productPages';
const ProductPage = lazy(() => import('./pages/ProductPage'));

const Features = lazy(() => import('./pages/Features'));
const LMS = lazy(() => import('./pages/LMS'));
const Security = lazy(() => import('./pages/Security'));
const DownloadApp = lazy(() => import('./pages/DownloadApp'));

// Company Pages
const About = lazy(() => import('./pages/About'));
const WhyIlmversity = lazy(() => import('./pages/WhyIlmversity'));
const CaseStudies = lazy(() => import('./pages/CaseStudies'));

// Case Study Detail Pages
const PSICaseStudy = lazy(() => import('./pages/case-studies/PSI'));
const NordicCaseStudy = lazy(() => import('./pages/case-studies/Nordic'));
const WhalesCaseStudy = lazy(() => import('./pages/case-studies/Whales'));
const EurekaCaseStudy = lazy(() => import('./pages/case-studies/Eureka'));
const DAMCaseStudy = lazy(() => import('./pages/case-studies/DAM'));
const ZICASCaseStudy = lazy(() => import('./pages/case-studies/ZICAS'));

// Support Pages
const Contact = lazy(() => import('./pages/Contact'));
// import FAQ from './pages/FAQ';

// Legal Pages
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const CookiePolicy = lazy(() => import('./pages/CookiePolicy'));

// Help / Support Guides
const EnableThirdPartyCookies = lazy(() => import('./pages/EnableThirdPartyCookies'));

// Blog Pages
const Blog = lazy(() => import('./pages/blog/Blog'));
const BlogPost = lazy(() => import('./pages/blog/BlogPost'));

// Error Pages
const NotFound = lazy(() => import('./pages/NotFound'));

// ============================================================================
// MAIN APP COMPONENT
// ============================================================================

import { ThemeProvider } from './context/ThemeContext';

const MouseFollower = lazy(() => import('./components/ui/MouseFollower'));
const ScrollToTopButton = lazy(() => import('./components/ui/ScrollToTopButton'));

function App({ location }) {
  const Router = location ? StaticRouter : BrowserRouter;
  return (
    <ThemeProvider>
      <Suspense fallback={null}>
        <MouseFollower />
        <ScrollToTopButton />
      </Suspense>
      <Router location={location}>
        {/* Scroll to top on route change */}
        <ScrollToTop />

        <Suspense fallback={<div className="min-h-screen" />}>
        <Routes>
          {landingPages.map(page => <Route key={page.path} path={page.path} element={<ProductPage page={page} />} />)}
          {/* ================================================================
              HOMEPAGE ROUTE
              ================================================================ */}
          <Route path="/" element={<Home />} />

          {/* ================================================================
              PRODUCT ROUTES
              These routes showcase Ilmversity's product features and offerings
              ================================================================ */}

          {/* Main features overview page */}
          <Route path="/features" element={<Features />} />

          {/* Learning Management System - SEO-optimized URL */}
          <Route path="/best-learning-management-system-lahore-karachi-pakistan" element={<LMS />} />

          {/* Security features and compliance information */}
          <Route path="/security" element={<Security />} />

          {/* Mobile app download page */}
          <Route path="/download-app" element={<DownloadApp />} />

          {/* ================================================================
              COMPANY ROUTES
              Information about Ilmversity as a company
              ================================================================ */}

          {/* About us page */}
          <Route path="/about-us" element={<About />} />

          {/* Why choose Ilmversity */}
          <Route path="/why-ilmversity" element={<WhyIlmversity />} />

          {/* Customer success stories and case studies */}
          <Route path="/case-studies" element={<CaseStudies />} />

          {/* Individual case study detail pages */}
          <Route path="/case-studies/psi" element={<PSICaseStudy />} />
          <Route path="/case-studies/nordic" element={<NordicCaseStudy />} />
          <Route path="/case-studies/whales" element={<WhalesCaseStudy />} />
          <Route path="/case-studies/eureka" element={<EurekaCaseStudy />} />
          <Route path="/case-studies/dam" element={<DAMCaseStudy />} />
          <Route path="/case-studies/zicas" element={<ZICASCaseStudy />} />

          {/* ================================================================
              SUPPORT ROUTES
              Customer support and contact pages
              ================================================================ */}

          {/* Contact us page */}
          <Route path="/contact" element={<Contact />} />

          {/* Request a free trial/demo (uses the same Contact component) */}
          <Route path="/request-free-trial" element={<Contact />} />

          {/* Frequently Asked Questions */}
          {/* <Route path="/faq" element={<FAQ />} /> */}

          {/* ================================================================
              LEGAL ROUTES
              Privacy policy and terms of service
              ================================================================ */}

          {/* Privacy policy */}
          <Route path="/privacy" element={<PrivacyPolicy />} />

          {/* Terms of service */}
          <Route path="/terms-of-service" element={<TermsOfService />} />

          {/* Cookie Policy */}
          <Route path="/cookie-policy" element={<CookiePolicy />} />

          {/* ================================================================
              HELP / SUPPORT GUIDES
              Step-by-step guides linked from the app/portal
              ================================================================ */}

          {/* How to enable third-party cookies in mobile Safari */}
          <Route path="/enabling-third-party-cookies-for-mobile-safari" element={<EnableThirdPartyCookies />} />

          {/* ================================================================
            RESOURCES ROUTES (formerly Blog)
            ================================================================ */}

          {/* Resources listing page */}
          <Route path="/resources" element={<Blog />} />

          {/* Individual resource post */}
          <Route path="/resources/:slug" element={<BlogPost />} />

          {/* ================================================================
            PRESS SHORT LINKS
            Brandable redirects we print on marketing material (video, decks).
            Keep the path stable — it is baked into rendered videos.
            ================================================================ */}

          {/* ilmversity.com/forbes → the Forbes feature (June 2025) */}
          <Route path="/forbes" element={<ExternalRedirect to={FORBES_ARTICLE} label="Forbes article" />} />

          {/* ================================================================
            404 NOT FOUND - CATCH ALL
            This must be the last route to catch all unmatched paths
            ================================================================ */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
      </Router>
    </ThemeProvider>
  );
}

export default App;
